import { Invoice } from '@/api/queries/project/useGetProjectInvoices';
import { ProjectPaymentScheduleStages } from '@/api/queries/project/useGetProjectPaymentSchedule';
import { Button, VerticalStepper } from '@payaca/ambrosia';

import { formatPercentage } from '@payaca/helpers/financeHelper';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { FC } from 'react';
import { InvoiceCard } from '../invoiceCard/InvoiceCard';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';

const getStepperPropsFromInvoice = (
  invoice?: Invoice | null
): Omit<React.ComponentProps<typeof VerticalStepper.Step>, 'title'> => {
  if (!invoice) {
    return {
      colour: 'gray',
    };
  }

  if (
    invoice.outstandingValue.value > 0 &&
    invoice.sentAt &&
    invoice.dueAt &&
    new Date(invoice.dueAt) < new Date()
  ) {
    return {
      icon: 'hourglass-01',
      colour: 'red',
      isIncomplete: true,
    };
  }

  if (invoice.status === 'PAID') {
    return {
      icon: 'check',
      colour: 'green',
    };
  }

  if (invoice.status === 'PARTIALLY_PAID') {
    return {
      icon: 'file-04',
      colour: 'yellow',
      isIncomplete: true,
    };
  }

  if (invoice.status === 'SENT') {
    return {
      icon: 'mail-02.3',
      colour: 'blue',
      isIncomplete: true,
    };
  }

  return {
    icon: 'file-04',
    colour: 'gray',
  };
};

export const ProjectPaymentScheduleStepper: FC<{
  stages: ProjectPaymentScheduleStages;
  onRecordPaymentSuccess?: () => void;
  requestCreateNextInvoice: () => void;
}> = ({ requestCreateNextInvoice, stages, onRecordPaymentSuccess }) => {
  if (stages.length === 0) {
    return null;
  }

  const createNextInvoiceIndex = stages.findIndex((stage) => !stage.invoice);

  return (
    <VerticalStepper>
      {stages.map((stage, index) => (
        <VerticalStepper.Step
          {...getStepperPropsFromInvoice(stage.invoice)}
          key={index}
          title={
            stage.stage
              ? `${stage.stage?.description} - ${formatPercentage(
                  stage.stage.percentageDue
                )}%`
              : `Stage ${index + 1}`
          }
        >
          {stage.invoice && (
            <InvoiceCard
              invoice={stage.invoice}
              onRecordPaymentSuccess={onRecordPaymentSuccess}
            />
          )}
          {createNextInvoiceIndex === index && (
            <PermissionGuard
              renderIfHasPermissions={[InvoicesPermissions.CREATE_INVOICE]}
            >
              <Button
                size="sm"
                variant="dashed"
                colour="white"
                onClick={requestCreateNextInvoice}
              >
                Create next Invoice
              </Button>
            </PermissionGuard>
          )}
        </VerticalStepper.Step>
      ))}
    </VerticalStepper>
  );
};
